import { useState } from "react";
import { Video, X, Loader2 } from "lucide-react";
import { uploadFile, fetchSignedUrl } from "@/lib/fileQueries";

/**
 * Master v5 Prompt 15 — optional video review. Reuses the EXISTING
 * FileAsset upload pipeline (target_type="review", target_id=rsbId) —
 * a non-coach actor uploading gets `member_private` visibility owned
 * by themselves automatically (see file_routes.py `_resolve_scope`
 * default branch), i.e. the same contextual/private security as every
 * other Rehearsal Room file. Nothing here invents a new storage path.
 */
export function VideoAttach({ workspaceId, rsbId, videoFasId, onAttached, onRemove }) {
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState(null);

  async function handleFile(e) {
    const file = e.target.files?.[0];
    if (!file || !rsbId) return;
    setUploading(true);
    setError(null);
    try {
      const init = await uploadFile({
        workspaceId, file, payload: { target_type: "review", target_id: rsbId, is_general_classification: false },
        onProgress: setProgress,
      });
      onAttached(init.asset.fas_id);
    } catch (err) {
      setError("Couldn't upload your video. Please try again.");
    } finally {
      setUploading(false);
    }
  }

  async function handlePreview() {
    const signed = await fetchSignedUrl(workspaceId, videoFasId);
    window.open(signed.url, "_blank", "noopener,noreferrer");
  }

  return (
    <div className="mb-4" data-testid="review-video-attach">
      <p className="text-sm font-medium text-ink mb-1.5">Video review (optional)</p>
      {videoFasId ? (
        <div className="flex items-center gap-3 border border-ink/15 rounded-lg px-3 py-2 bg-white/70">
          <Video size={18} className="text-terracotta shrink-0" />
          <button onClick={handlePreview} className="text-sm text-terracotta hover:underline" data-testid="review-video-preview-button">
            Preview attached video
          </button>
          <button onClick={onRemove} className="ml-auto text-ink-soft hover:text-ink" data-testid="review-video-remove-button" aria-label="Remove video">
            <X size={16} />
          </button>
        </div>
      ) : uploading ? (
        <p className="text-sm text-ink-soft flex items-center gap-2" data-testid="review-video-uploading">
          <Loader2 size={14} className="animate-spin" /> Uploading… {progress}%
        </p>
      ) : (
        <label className="inline-flex items-center gap-2 text-sm text-terracotta hover:text-terracotta/80 cursor-pointer" data-testid="review-video-upload-label">
          <Video size={16} /> Attach a short video
          <input type="file" accept="video/*" className="hidden" onChange={handleFile} data-testid="review-video-upload-input" />
        </label>
      )}
      {error && <p className="text-xs text-red-600 mt-1" data-testid="review-video-error">{error}</p>}
    </div>
  );
}
